import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "panel-surface p-s4" }
const _hoisted_2 = { class: "md:flex justify-content-between w-full" }
const _hoisted_3 = { class: "flex align-items-center" }
const _hoisted_4 = { class: "title-sm" }
const _hoisted_5 = { class: "text-gradient-light" }
const _hoisted_6 = { class: "flex align-items-center gutter-x-s1 mt-s1" }
const _hoisted_7 = { class: "caption" }
const _hoisted_8 = {
  key: 1,
  class: "h3"
}
const _hoisted_9 = { class: "md:align-self-center" }

import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { AutoStaking } from '@/sdk/entities/autoStaking';
import { format } from '@/utils/format';
import { toWei } from '@/sdk/utils';
import Icon from '@/components/icon/Icon.vue';
import Confirmation from './Confirmation.vue';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';
import Spinner from '@/components/Spinner.vue';
import WalletConnect from '@/views/layout/wallet/WalletConnect.vue';
import BluesTokenIcon from '@/components/token/BluesTokenIcon.vue';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useAllSteps } from '@/composables/useAllSteps';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useStakingSteps } from '@/store/modules/staking/useStakingSteps';
import { useStakingClaim } from '@/store/modules/staking/claim/useStakingClaim';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import InfoMessage from '@/components/InfoMessage.vue';
import {
  calculateMinADABalanceToCardano,
  TX_FEE_CARDANO_IN_ADA,
} from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import BigNumber from 'bignumber.js';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { checkIfNetworkIdDisabledToAdd } from '@/helpers/network-disable.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'AutoBounty',
  props: {
    autoPool: null
  },
  emits: ["sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  autoPool: AutoStaking;
}





const { t } = useI18n();
const { state } = useStore();

const { walletState } = useEVMWallet();
const allStepsState = useAllSteps();
const { getTokenByAddressAndChainId } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const { stakingSteps, setStaking, $reset: resetStakingSteps } = useStakingSteps();
const { checkClaimForBridgeFromMilkomeda, setClaimFromPool, doClaim } = useStakingClaim();

const isShowDialog = ref(false);
const inProgress = ref(false);

const isAutoStakingStoreReady = computed(() => state.autoStaking.isStoreReady);
const poolToken = computed(() => {
  if (!DEFAULT_NETWORK_ID) return;
  return getTokenByAddressAndChainId(props.autoPool.token, +DEFAULT_NETWORK_ID);
});

const adaBalance = computed(
  () =>
    balanceByTokenSymbolAndChainId('ADA', +DEFAULT_CARDANO_CHAIN_ID).value?.balance.toExact() ?? 0,
);
const minADABalance = computed(() =>
  calculateMinADABalanceToCardano(TX_FEE_CARDANO_IN_ADA as BigNumber),
);
const isShowNotEnoughCurrencyForFee = computed(
  () =>
    ENABLE_FAKE_CARDANO_NETWORK &&
    minADABalance.value.gt(adaBalance.value) &&
    walletState.isInjected,
);
const isNetworkDisabledToAdd = computed(() => checkIfNetworkIdDisabledToAdd(DEFAULT_NETWORK_ID));
const isClaimDisabled = computed(() => {
  return (
    inProgress.value ||
    !isAutoStakingStoreReady.value ||
    allStepsState.isInProgress.state ||
    isShowNotEnoughCurrencyForFee.value ||
    isNetworkDisabledToAdd.value
  );
});

function printClaim() {
  return format(props.autoPool.printClaim);
}

function claimInUSD() {
  return props.autoPool.printClaimUSD.toString();
}

function onDialogOpen() {
  isShowDialog.value = true;
}

function onDialogClose() {
  isShowDialog.value = false;
}

// CLAIM
async function onClaimWSC() {
  if (stakingSteps.isShown) {
    resetStakingSteps();
  }

  if (!poolToken.value) return;

  setStaking('auto', 'claim');

  const amountInWei = toWei(
    props.autoPool.printClaim.toFixed(),
    poolToken.value.decimals,
  ).toString();
  checkClaimForBridgeFromMilkomeda(amountInWei, poolToken.value);

  setClaimFromPool(props.autoPool, poolToken.value);

  await nextTick();

  emits('sendTransaction');
}

async function onClaim() {
  if (!poolToken.value) return;

  inProgress.value = true;

  try {
    setClaimFromPool(props.autoPool, poolToken.value);
    await doClaim();
  } finally {
    inProgress.value = false;
  }
}

async function onClaimTransaction() {
  onDialogClose();

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    await onClaimWSC();
    return;
  }

  await onClaim();
}

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(Icon, {
          name: "coins",
          class: "text-primary mr-s4 opacity-80 flex-shrink-0",
          size: _unref(state).layout.isMobile ? '56' : '44'
        }, null, 8, ["size"]),
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('yieldPool.autoBounty.caption')), 1),
            _createVNode(TooltipIcon, {
              tooltip: _unref(t)('yieldPool.autoBounty.tooltip')
            }, null, 8, ["tooltip"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(t)('yieldPool.autoBounty.bounty')) + ":", 1),
            (!_unref(isAutoStakingStoreReady))
              ? (_openBlock(), _createBlock(_component_Skeleton, { key: 0 }))
              : (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(printClaim()), 1)),
            _createVNode(BluesTokenIcon, { size: "16" })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_9, [
        (!_unref(walletState).isInjected)
          ? (_openBlock(), _createBlock(WalletConnect, {
              key: 0,
              ref: "wallet",
              "button-class": "w-full button-primary mt-s6 md:mt-s0 md:ml-auto"
            }, null, 512))
          : (_openBlock(), _createBlock(_component_Button, {
              key: 1,
              class: "w-full button-primary mt-s6 md:mt-s0 md:w-7rem md:min-w-max",
              onClick: onDialogOpen,
              disabled: _unref(isClaimDisabled)
            }, {
              default: _withCtx(() => [
                (inProgress.value)
                  ? (_openBlock(), _createBlock(Spinner, {
                      key: 0,
                      color: "white",
                      size: "24",
                      class: "mr-s2"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" " + _toDisplayString(inProgress.value ? _unref(t)('inProgress') : _unref(t)('yieldPool.autoBounty.claim')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
      ])
    ]),
    (_unref(isShowNotEnoughCurrencyForFee))
      ? (_openBlock(), _createBlock(InfoMessage, {
          key: 0,
          type: "error",
          "as-panel": "",
          class: "mt-s3"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.notEnoughCurrencyForFee', { amount: _unref(minADABalance), symbol: 'ADA' })), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_Dialog, {
      visible: isShowDialog.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((isShowDialog).value = $event)),
      header: _unref(t)('yieldPool.autoBounty.confirmDialog.header'),
      dismissableMask: "",
      modal: true,
      "content-class": _unref(state).layout.isMobile ? 'scroll-container' : ''
    }, {
      default: _withCtx(() => [
        _createVNode(Confirmation, {
          amount: printClaim(),
          amountUsd: claimInUSD(),
          "in-progress": inProgress.value,
          cancel: onDialogClose,
          confirm: onClaimTransaction
        }, null, 8, ["amount", "amountUsd", "in-progress"])
      ]),
      _: 1
    }, 8, ["visible", "header", "content-class"])
  ]))
}
}

})